import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { TodoApi } from "../api";
import { TodoData, CreateTodoInput } from "../../../types";

type TodoUpdates = Omit<Partial<TodoData>, "scheduledDate"> & {
  // null clears the field (e.g. un-scheduling from Today) — see
  // formatUpdateRequest on the backend for why this needs its own sentinel.
  scheduledDate?: string | null;
};

const dataContext = createContext<{
  todos: TodoData[];
  loading: boolean;
  createTodo: (input: Omit<CreateTodoInput, "id">) => Promise<void>;
  updateTodo: (todo: TodoData, updates: TodoUpdates) => Promise<void>;
  deleteTodo: (todo: TodoData) => Promise<void>;
}>({
  todos: [],
  loading: true,
  createTodo: async () => {},
  updateTodo: async () => {},
  deleteTodo: async () => {},
});

const nowISO = () => new Date().toISOString();

export const DataProvider = ({ children }: { children: React.ReactNode }) => {
  const [todos, setTodos] = useState<TodoData[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await TodoApi.getAll();
    if (result.success && result.data) {
      setTodos(result.data.todos);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Optimistic: the client generates the id and renders immediately, then
  // reconciles with whatever the server actually stored (server sets
  // createdAt/updatedAt, which is the source of truth for the sort key).
  const createTodo = async (input: Omit<CreateTodoInput, "id">) => {
    const id = crypto.randomUUID();
    const optimistic: TodoData = {
      type: "todo",
      id,
      userId: "",
      title: input.title,
      notes: input.notes,
      context: input.context,
      parentId: input.parentId,
      priority: input.priority,
      complexity: input.complexity,
      done: false,
      order: -Date.now(),
      timesScheduled: input.scheduledDate ? 1 : 0,
      scheduledDate: input.scheduledDate,
      createdAt: nowISO(),
      updatedAt: nowISO(),
    };
    setTodos((t) => [...t, optimistic]);
    const result = await TodoApi.createTodo({ ...input, id });
    if (result.success && result.data) {
      setTodos((t) => t.map((existing) => (existing.id === id ? result.data : existing)));
    }
  };

  const updateTodo = async (todo: TodoData, updates: TodoUpdates) => {
    // Locally, "no scheduledDate" is just absent (TodoData has no `null`
    // variant) — `null` is only meaningful as a wire signal to the API.
    const { scheduledDate, ...restUpdates } = updates;
    const localUpdates: Partial<TodoData> = { ...restUpdates };
    if ("scheduledDate" in updates) {
      localUpdates.scheduledDate = scheduledDate === null ? undefined : scheduledDate;
    }
    setTodos((t) =>
      t.map((existing) =>
        existing.id === todo.id ? { ...existing, ...localUpdates } : existing,
      ),
    );
    await TodoApi.updateTodo({
      id: todo.id,
      createdAt: todo.createdAt,
      ...updates,
    });
  };

  const deleteTodo = async (todo: TodoData) => {
    setTodos((t) => t.filter((existing) => existing.id !== todo.id));
    await TodoApi.deleteTodo(todo.id, todo.createdAt);
  };

  return (
    <dataContext.Provider value={{ todos, loading, createTodo, updateTodo, deleteTodo }}>
      {children}
    </dataContext.Provider>
  );
};

export const useData = () => useContext(dataContext);
